import { listDepartments, listUsers, type UserRow } from '@/features/configuracoes/api';
import { ApiError } from '@/shared/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { bulkTransferTickets } from '../api';
import { invalidateServerTruthLists } from '../inbox-debug';
import { getSocket } from '../socket';

interface Props {
  open: boolean;
  ticketIds: number[];
  onClose: () => void;
  onSuccessAll: () => void;
  /** Ids que de fato transferiram (também no sucesso parcial) — a conversa
   *  aberta, se estiver entre eles, sai da tela igual ao transferir 1. */
  onSucceeded?: (ticketIds: number[]) => void;
}

/**
 * Modal centralizado pra transferir vários atendimentos ao mesmo tempo.
 * Aceita user_id e/ou department_id (pelo menos um — backend valida).
 * Confirmação em 2 etapas + toast de resultado.
 */
export function BulkTransferModal({ open, ticketIds, onClose, onSuccessAll, onSucceeded }: Props) {
  const qc = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Só ONLINE por padrão; toggle inclui offline (atribuir a quem volta depois).
  const [includeOffline, setIncludeOffline] = useState(false);

  const usersQ = useQuery({
    queryKey: ['settings-users'],
    queryFn: () => listUsers(),
    enabled: open,
  });
  const deptsQ = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: open,
  });

  const mut = useMutation({
    mutationFn: () => {
      const input: {
        ticket_ids: number[];
        user_id?: number;
        department_id?: number;
        require_online?: boolean;
      } = {
        ticket_ids: ticketIds,
      };
      if (selectedUserId) input.user_id = selectedUserId;
      if (selectedDepartmentId) input.department_id = selectedDepartmentId;
      if (selectedUserId && !includeOffline) input.require_online = true;
      return bulkTransferTickets(input);
    },
    onSuccess: (r) => {
      invalidateServerTruthLists(qc, 'bulkTransferMut');
      if (r.succeeded.length > 0) onSucceeded?.(r.succeeded);
      if (r.failed.length === 0) {
        toast.success(
          `${r.succeeded.length} ${r.succeeded.length === 1 ? 'atendimento transferido' : 'atendimentos transferidos'} com sucesso`,
        );
        reset();
        onSuccessAll();
        onClose();
      } else {
        toast.warning(
          `${r.succeeded.length} transferido${r.succeeded.length === 1 ? '' : 's'}, ${r.failed.length} falharam`,
        );
      }
    },
    onError: (err) => {
      toast.error('Erro ao transferir atendimentos', {
        description:
          err instanceof ApiError
            ? (err.problem.detail ?? err.problem.title)
            : err instanceof Error
              ? err.message
              : undefined,
      });
    },
  });

  const reset = (): void => {
    setSelectedUserId(null);
    setSelectedDepartmentId(null);
    setConfirming(false);
    setError(null);
    setIncludeOffline(false);
  };

  // Se o atendente selecionado deixou de ser visível (toggle offline desligado),
  // limpa a seleção pra não ficar com value órfão no select.
  useEffect(() => {
    if (selectedUserId == null) return;
    const u = (usersQ.data ?? []).find((x) => x.id === selectedUserId);
    const visible = u != null && u.status === 'active' && (includeOffline || u.online);
    if (!visible) setSelectedUserId(null);
  }, [usersQ.data, includeOffline, selectedUserId]);

  // Presença ao vivo enquanto o modal está aberto.
  useEffect(() => {
    if (!open) return;
    const socket = getSocket();
    const onPresence = (p: { user_id: number; online: boolean }): void => {
      qc.setQueryData<UserRow[]>(['settings-users'], (prev) =>
        prev?.map((u) => (u.id === p.user_id ? { ...u, online: p.online } : u)),
      );
    };
    socket.on('presence.changed', onPresence);
    return () => {
      socket.off('presence.changed', onPresence);
    };
  }, [open, qc]);

  if (!open) return null;

  const submitting = mut.isPending;
  const activeUsers = (usersQ.data ?? []).filter((u) => u.status === 'active');
  // Lista visível no dropdown: online por padrão. `activeUsers` (completa) segue
  // pra resolver o nome no passo de confirmação.
  const visibleUsers = includeOffline ? activeUsers : activeUsers.filter((u) => u.online);
  const selectedUser = activeUsers.find((u) => u.id === selectedUserId);
  const selectedDept = (deptsQ.data ?? []).find((d) => d.id === selectedDepartmentId);

  const handleClose = (): void => {
    if (submitting) return;
    reset();
    onClose();
  };

  const proceedToConfirm = (): void => {
    setError(null);
    if (!selectedUserId && !selectedDepartmentId) {
      setError('Selecione um departamento ou um atendente');
      return;
    }
    setConfirming(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
      onKeyDown={(e) => e.key === 'Escape' && handleClose()}
    >
      <div
        className="w-[460px] max-w-[92vw] rounded-md border border-n-200 bg-n-0 p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {!confirming ? (
          <>
            <h2 className="font-bold text-[16px] text-n-900">
              Transferir {ticketIds.length}{' '}
              {ticketIds.length === 1 ? 'atendimento' : 'atendimentos'}
            </h2>
            <p className="mt-1 text-[13px] text-n-500">
              Escolha pra quem transferir. Pode ser um departamento, um atendente, ou ambos.
            </p>

            {/* Departamento antes do atendente — mesma ordem da transferência de 1. */}
            <div className="mt-4 flex flex-col gap-3.5">
              <label className="flex flex-col gap-1.5">
                <span className="font-semibold text-[12px] text-n-700">Departamento</span>
                <select
                  value={selectedDepartmentId ?? ''}
                  onChange={(e) =>
                    setSelectedDepartmentId(e.target.value ? Number(e.target.value) : null)
                  }
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50"
                  disabled={deptsQ.isLoading}
                >
                  <option value="">— Não atribuir a departamento —</option>
                  {(deptsQ.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="font-semibold text-[12px] text-n-700">Atendente</span>
                <select
                  value={selectedUserId ?? ''}
                  onChange={(e) =>
                    setSelectedUserId(e.target.value ? Number(e.target.value) : null)
                  }
                  className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50"
                  disabled={usersQ.isLoading}
                >
                  <option value="">— Não atribuir a atendente —</option>
                  {visibleUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email}){u.online ? ' · online' : ' · offline'}
                    </option>
                  ))}
                </select>
              </label>

              <label className="-mt-1 flex cursor-pointer items-center gap-2 text-[11px] text-n-600">
                <input
                  type="checkbox"
                  checked={includeOffline}
                  onChange={(e) => setIncludeOffline(e.target.checked)}
                />
                Mostrar atendentes offline
              </label>

              {error && (
                <div className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
                  {error}
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md border border-n-300 bg-n-0 px-4 py-2 font-bold text-[12px] text-n-700 uppercase tracking-[0.05em] hover:bg-n-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={proceedToConfirm}
                className="rounded-md bg-brand-500 px-4 py-2 font-bold text-[12px] text-white uppercase tracking-[0.05em] hover:bg-brand-600"
              >
                Transferir{' '}
                {ticketIds.length === 1 ? 'atendimento' : 'todos'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="font-bold text-[16px] text-n-900">Confirmar transferência</h2>
            <p className="mt-2 text-[13px] text-n-700">
              Você vai transferir <strong>{ticketIds.length}</strong>{' '}
              {ticketIds.length === 1 ? 'atendimento' : 'atendimentos'}
              {selectedUser && (
                <>
                  {' '}
                  pra <strong>{selectedUser.name}</strong>
                </>
              )}
              {selectedDept && (
                <>
                  {selectedUser ? ' no departamento' : ' pro departamento'}{' '}
                  <strong>{selectedDept.name}</strong>
                </>
              )}
              .
            </p>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={submitting}
                className="rounded-md border border-n-300 bg-n-0 px-4 py-2 font-bold text-[12px] text-n-700 uppercase tracking-[0.05em] hover:bg-n-50 disabled:opacity-50"
              >
                Voltar
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => mut.mutate()}
                className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 font-bold text-[12px] text-white uppercase tracking-[0.05em] hover:bg-brand-600 disabled:opacity-50"
              >
                {submitting && <Loader2 size={12} className="animate-spin" />}
                {submitting ? 'Transferindo…' : 'Sim, transferir'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
